import { useState } from "react";
import { Plus, Minus } from "lucide-react";
import { useScrollReveal } from "../../hooks/useScrollReveal";

const faqs = [
  {
    q: "What is iQ Hub?",
    a: (
      <>
        iQ Hub is a global B2B executive conference organiser founded in 2009,
        operating under IQ International Pte. Ltd. We design curated industry
        forums for senior decision-makers, prioritising meaningful dialogue
        over mass-scale trade shows.
      </>
    ),
  },
  {
    q: "How long has iQ Hub been operating?",
    a: (
      <>
        iQ Hub was established in 2009 and has over 17 years of experience
        organising executive conferences worldwide.
      </>
    ),
  },
  {
    q: "What industries does iQ Hub focus on?",
    a: (
      <>
        We organise conferences across eight core sectors: Energy &amp; Power,
        Oil &amp; Gas, Water &amp; Utilities, Transportation &amp; EV, Battery
        &amp; Materials, Decarbonization, Agriculture &amp; Food, and Pharma
        &amp; Healthcare. Each portfolio is developed around emerging industry
        priorities and senior-level participation.
      </>
    ),
  },
  {
    q: "Where does iQ Hub operate?",
    a: (
      <>
        iQ Hub hosts conferences in 34+ cities across 14+ countries worldwide,
        including the United States, United Kingdom, Germany, Netherlands,
        Norway, Spain, France, Belgium, UAE, Australia, Canada, Brazil,
        Argentina and Singapore. We operate wherever industry leadership and
        innovation converge.
      </>
    ),
  },
  {
    q: "Who attends iQ Hub conferences?",
    a: (
      <>
        Our events are curated for senior professionals, including C-suite
        executives, Vice Presidents, Directors and strategic decision-makers.
        We prioritise quality of audience and structured interaction over
        large-scale exhibition formats.
      </>
    ),
  },
  {
    q: "How can I verify an official iQ Hub email or outreach?",
    a: (
      <>
        All official iQ Hub communications are sent from verified company
        domains. To confirm the legitimacy of any outreach, please contact us
        directly at{" "}
        <a
          href="mailto:support@iq-hub.com"
          className="text-[#C8A951] hover:underline"
        >
          support@iq-hub.com
        </a>
        . We encourage direct verification whenever there is uncertainty.
      </>
    ),
  },
  {
    q: "How can I get involved as a delegate, speaker, exhibitor or partner?",
    a: (
      <>
        If you are interested in attending, speaking, exhibiting or partnering
        with iQ Hub, please contact us at{" "}
        <a
          href="mailto:support@iq-hub.com"
          className="text-[#C8A951] hover:underline"
        >
          support@iq-hub.com
        </a>
        . Our team will connect you with the appropriate event or department.
      </>
    ),
  },
  {
    q: "Where is iQ Hub registered?",
    a: (
      <>
        iQ Hub operates under IQ International Pte. Ltd., a company
        headquartered in Seattle with a registered office in Singapore.
      </>
    ),
  },
  {
    q: "How does iQ Hub handle personal data?",
    a: (
      <>
        Our data practices are outlined in our Privacy Policy, which explains
        how attendee and partner information is handled in accordance with
        applicable regulations. For full details, please refer to our{" "}
        <a
          href="https://www.iq-hub.com/privacy-policy"
          className="text-[#C8A951] hover:underline"
        >
          Privacy Policy
        </a>{" "}
        page.
      </>
    ),
  },
];

export const FaqSection = () => {
  const [openIndex, setOpenIndex] = useState(-1);
  const [ref, isVisible] = useScrollReveal();

  const toggle = (i) => setOpenIndex((current) => (current === i ? -1 : i));

  return (
    <section
      id="faq"
      ref={ref}
      className="py-24 bg-[#FAFAF8]"
      data-testid="faq-section"
    >
      <div
        className={`max-w-7xl mx-auto px-6 lg:px-8 transition-all duration-700 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        {/* Section Header — matches JoinExperience pattern */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-[#C8A951] text-sm font-semibold tracking-[0.15em] uppercase mb-4 block">
            FAQ
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-[#0F172A] tracking-tight mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-[#64748B] leading-relaxed">
            Find answers to common questions about iQ Hub, our events, and how we work.
          </p>
        </div>

        {/* Accordion */}
        <div className="divide-y divide-[#E2E8F0] border-y border-[#E2E8F0]">
          {faqs.map((item, i) => {
            const isOpen = openIndex === i;
            return (
              <div key={i} data-testid={`faq-item-${i}`}>
                <button
                  type="button"
                  onClick={() => toggle(i)}
                  aria-expanded={isOpen}
                  aria-controls={`faq-panel-${i}`}
                  className="w-full flex items-center justify-between gap-6 text-left py-6 group"
                  data-testid={`faq-trigger-${i}`}
                >
                  <span className="text-base lg:text-lg font-semibold text-[#0F172A] group-hover:text-[#C8A951] transition-colors">
                    {item.q}
                  </span>
                  <span
                    className={`shrink-0 flex items-center justify-center transition-colors duration-200 ${
                      isOpen
                        ? "text-[#C8A951]"
                        : "text-[#0F172A] group-hover:text-[#C8A951]"
                    }`}
                  >
                    {isOpen ? (
                      <Minus className="w-4 h-4" strokeWidth={1.75} />
                    ) : (
                      <Plus className="w-4 h-4" strokeWidth={1.75} />
                    )}
                  </span>
                </button>
                <div
                  id={`faq-panel-${i}`}
                  role="region"
                  className={`grid transition-all duration-300 ease-in-out ${
                    isOpen
                      ? "grid-rows-[1fr] opacity-100 pb-6"
                      : "grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <div className="overflow-hidden">
                    <p
                      className="text-base text-[#475569] leading-relaxed pr-12"
                      data-testid={`faq-answer-${i}`}
                    >
                      {item.a}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
